
import type { ReactElement, ReactNode } from 'react';
import { Card, Heading, Stack, Text } from '@client/shared/design';
import type { Role, Session } from '@client/shared/auth/session.ts';
import { hasAnyRole } from '@client/shared/auth/session.ts';
import { Bookings, ListingDetails, listingIdFromPath } from '@client/features/scheduling';
import {
  Home,
  Listings,
  NewListing,
  EditListing,
  editListingIdFromPath,
} from '@client/features/listings';
import { MyAccount } from '@client/features/account';
import { UsersPage } from '@client/features/users';
import { GroupSettings } from '@client/features/group';
import { PATHS } from '@client/shared/router/paths.ts';
import { Redirect } from '@client/shared/router/Redirect.tsx';

type RouteRender = (ctx: RouteRenderContext) => ReactElement;

interface RouteRenderContext {
  readonly pathname: string;
  readonly navigate: (to: string) => void;
}

interface RouteDef {
  readonly match: (pathname: string) => boolean;
  readonly roles: readonly Role[];
  readonly render: RouteRender;
}

const LISTINGS_PATH = PATHS.listings;
const NEW_LISTING_PATH = PATHS.newListing;

// 'agent' here means the agent<admin<owner ladder (hasRole ranks them all above agent).
// A billing-only session sits outside the ladder and holds no agent-tier data access on
// the server, so member pages would only render authz errors for it.
const ROUTES: readonly RouteDef[] = [
  {
    match: (p) => p === PATHS.home,
    roles: ['agent'],
    render: () => <Home />,
  },
  {
    match: (p) => p === NEW_LISTING_PATH,
    roles: ['admin', 'owner'],
    render: ({ navigate }) => (
      <NewListing
        onCreated={(listing) => navigate(`${LISTINGS_PATH}/${listing.id}`)}
        onCancel={() => navigate(LISTINGS_PATH)}
      />
    ),
  },
  {
    match: (p) => p === LISTINGS_PATH,
    roles: ['agent'],
    render: () => <Listings toggleable />,
  },
  {
    match: (p) => p.startsWith(`${LISTINGS_PATH}/`) && editListingIdFromPath(p) != null,
    roles: ['admin', 'owner'],
    render: ({ pathname, navigate }) => {
      const id = editListingIdFromPath(pathname) ?? undefined;
      return (
        <EditListing
          listingId={id}
          onSaved={(listingId) => navigate(`${LISTINGS_PATH}/${listingId}`)}
          onCancel={() => navigate(id ? `${LISTINGS_PATH}/${id}` : LISTINGS_PATH)}
        />
      );
    },
  },
  {
    match: (p) =>
      p.startsWith(`${LISTINGS_PATH}/`) && !p.endsWith('/edit') && listingIdFromPath(p) != null,
    roles: ['agent'],
    render: ({ pathname }) => <ListingDetails listingId={listingIdFromPath(pathname) ?? undefined} />,
  },
  {
    match: (p) => p === PATHS.bookings,
    roles: ['agent'],
    render: () => <Bookings />,
  },
  {
    match: (p) => p === PATHS.users,
    roles: ['admin', 'owner'],
    render: () => <UsersPage />,
  },
  {
    match: (p) => p === PATHS.groupSettings,
    roles: ['admin', 'owner', 'billing'],
    render: () => <GroupSettings />,
  },
  {
    match: (p) => p === PATHS.account,
    roles: [],
    render: () => <MyAccount />,
  },
];

export interface MatchedRoute {
  readonly element: ReactElement;
}

export function matchRoute(
  pathname: string,
  session: Session,
  navigate: (to: string) => void,
): MatchedRoute {
  const route = ROUTES.find((r) => r.match(pathname));
  if (!route) {
    return { element: <NotFoundRoute /> };
  }
  if (route.roles.length > 0 && !hasAnyRole(session, ...route.roles)) {
    // A billing-only session can use nothing but group settings and the account page —
    // land it on settings (its home) instead of a dead end.
    if (!hasAnyRole(session, 'agent')) {
      return { element: <Redirect to={PATHS.groupSettings} replace /> };
    }
    return { element: <NoAccessRoute /> };
  }
  return { element: route.render({ pathname, navigate }) };
}

function MessageCard({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <Card pad="lg">
      <Stack gap={3} align="center">
        <Heading level={2} as="h1">
          {heading}
        </Heading>
        <Text tone="soft">{children}</Text>
      </Stack>
    </Card>
  );
}

function NotFoundRoute() {
  return (
    <MessageCard heading="Page not found">
      That page doesn’t exist. <a href="/">Go back home</a>.
    </MessageCard>
  );
}

function NoAccessRoute() {
  return (
    <MessageCard heading="You don’t have access to this page">
      Ask your group’s admin if you need it. <a href="/">Back home</a>.
    </MessageCard>
  );
}
